import React from "react"
import * as XLSX from "xlsx"
import { useSelector } from "react-redux"
import { selectUser } from "../../core/data/redux/slice/user"
import { selectCurrentBranch } from "../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel, absoluteCampusLogoUrl } from "../../utils/campusLogos"
import { InvoiceTableData } from "./accounts-invoices"
import {
  computeCashierReportTotals,
  CashierReportTables,
  getSectionName,
  formatMonth,
  formatDate,
} from "./cashierReportShared"

interface InvoicesPrintModalProps {
  show: boolean
  onClose: () => void
  invoices: InvoiceTableData[]
  printRef: React.RefObject<HTMLDivElement>
  onPrint: () => void
  filters: {
    startDate?: string
    endDate?: string
    system?: string
    status?: string
    session?: any
  }
  exportType?: "pdf" | "excel"
}

const InvoicesPrintModal: React.FC<InvoicesPrintModalProps> = ({
  show,
  onClose,
  invoices,
  printRef,
  filters,
  exportType = "pdf",
}) => {
  const user = useSelector(selectUser)
  // Report is scoped to whichever campus is currently selected, not a
  // specific student's own campus — same pattern as attendanceReport.tsx.
  const currentBranch = useSelector(selectCurrentBranch)
  const brandName = campusBrandName(currentBranch)

  const handlePrintInNewWindow = () => {
    const printContent = printRef.current?.innerHTML
    if (!printContent) return

    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Fees Report</title>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 20px;
            font-size: 11px;
            color: #000;
          }
          .print-container {
            max-width: 100%;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          .header h2 {
            margin: 0;
            font-size: 24px;
            font-weight: bold;
          }
          .header h4 {
            margin: 5px 0 15px 0;
            font-size: 18px;
            font-weight: normal;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
          }
          .info-item {
            text-align: center;
            flex: 1;
          }
          .info-item strong {
            display: block;
            margin-bottom: 5px;
          }
          table {
            width: 100% !important;
            border-collapse: collapse;
            margin-bottom: 20px;
            page-break-inside: auto;
            min-width: 0 !important;
            table-layout: auto;
            font-size: 9px !important;
          }
          th {
            background-color: #f2f2f2;
            border: 1px solid #ddd;
            padding: 3px 2px !important;
            text-align: left;
            font-weight: bold;
            word-break: break-word;
            white-space: normal;
          }
          td {
            border: 1px solid #ddd;
            padding: 3px 2px !important;
            vertical-align: top;
            word-break: break-word;
            white-space: normal;
          }
          .text-center {
            text-align: center;
          }
          .text-end {
            text-align: right;
          }
          .text-start {
            text-align: left;
          }
          .summary-table th,
          .summary-table td {
            padding: 8px;
          }
          .footer {
            margin-top: 30px;
            border-top: 1px solid #ddd;
            padding-top: 10px;
            font-size: 10px;
            color: #666;
          }
          .page-break {
            page-break-before: always;
          }
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              padding: 5px;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          ${printContent}
        </div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 100);
          };
        </script>
      </body>
      </html>
    `)
    printWindow.document.close()
  }

  const getSessionName = (session: any): string => {
    if (!session) return "2025-2026"

    if (typeof session === "string") return session

    if (typeof session === "object") {
      if (session.name) return session.name

      if (session.startDate) {
        try {
          const startDate = new Date(session.startDate)
          const endDate = session.endDate ? new Date(session.endDate) : null

          if (endDate) {
            const startYear = startDate.getFullYear()
            const endYear = endDate.getFullYear()
            return `${startYear}-${endYear}`
          } else {
            const year = startDate.getFullYear()
            const nextYear = year + 1
            return `${year}-${nextYear}`
          }
        } catch (error) {
          console.error("Error parsing session date:", error)
        }
      }

      if (session._id) return `Session ${session._id.substring(0, 6)}`
    }

    return "2025-2026"
  }

  const {
    activeInvoices,
    totalCollected,
    totalDiscount,
    totalFine,
    totalSubTotal,
    tuitionBreakdown,
    feeGroupBreakdownMap,
  } = computeCashierReportTotals(invoices)
  const totals = {
    activeInvoices,
    totalCollected,
    totalDiscount,
    totalFine,
    totalSubTotal,
    tuitionBreakdown,
    feeGroupBreakdownMap,
  }

  if (!show) return null

  return (
    <div
      className={`modal fade ${show ? "show d-block" : ""}`}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {exportType === "pdf"
                ? "Print Cashier Report"
                : "Export Invoices Report"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body p-0">
            <div ref={printRef} className="p-3">
              <div className="text-center border-bottom">
                <img
                  src={absoluteCampusLogoUrl(currentBranch?.blackLogoUrl)}
                  alt={brandName}
                  style={{ width: "70px", height: "70px", objectFit: "contain" }}
                  className="mb-1"
                />
                <h2
                  className="mb-1"
                  style={{ fontSize: "24px", fontWeight: "bold" }}
                >
                  {brandName}
                </h2>
                {campusBranchLabel(currentBranch) ? (
                  <p className="mb-1" style={{ fontSize: "14px" }}>
                    {campusBranchLabel(currentBranch).toUpperCase()}
                  </p>
                ) : null}
                <h4
                  className="mb-2"
                  style={{ fontSize: "18px", fontWeight: "normal" }}
                >
                  Invoice Report ({getSessionName(filters.session)})
                </h4>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "1px",
                  }}
                >
                  <p>
                    <strong>Generated by:</strong> {user?.firstName || ""}{" "}
                    {user?.lastName}
                  </p>
                  <p>
                    <strong>Date Range:</strong> {formatDate(filters.startDate)}{" "}
                    — {formatDate(filters.endDate)}
                  </p>
                </div>
              </div>
              <CashierReportTables totals={totals} />
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={onClose}
            >
              Close
            </button>
            {exportType === "pdf" ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handlePrintInNewWindow}
              >
                <i className="ti ti-external-link me-2"></i>
                Print
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-success"
                onClick={() => {
                  const ws = XLSX.utils.json_to_sheet(
                    invoices.map((invoice, index) => {
                      const student = invoice.student
                      const tuitionItems =
                        invoice.invoiceItems?.filter((item: any) =>
                          item.feeGroup?.name
                            ?.toLowerCase()
                            .includes("tuition"),
                        ) || []
                      const uniqueMonths = Array.from(
                        new Set(
                          tuitionItems
                            .map((item: any) => item.month)
                            .filter(Boolean),
                        ),
                      ).join(", ")
                      const allFeeGroupNames = Array.from(
                        new Set(
                          invoice.invoiceItems
                            ?.map((item: any) => item.feeGroup?.name)
                            .filter(Boolean),
                        ),
                      ).join(", ")
                      return {
                        "S No.": index + 1,
                        "GR No.": invoice.student?.grNumber || "N/A",
                        "Student Name":
                          `${student?.user?.firstName || ""} ${student?.user?.lastName || ""}`.trim(),
                        "Receipt No.": invoice.invoiceNumber,
                        Class: invoice.class?.name || "N/A",
                        Section: getSectionName(invoice),
                        "Month Paid": uniqueMonths,
                        Details: allFeeGroupNames || "-",
                        Date: new Date(invoice.createdAt).toLocaleDateString(),
                        "Sub Total": invoice.subTotal,
                        Discount:
                          (invoice.discount?.manual || 0) +
                          (invoice.discount?.profile || 0),
                        Fine: invoice.fine || 0,
                        "Paid Amount": invoice.total,
                        Status: invoice.status,
                      }
                    }),
                  )

                  const wb = XLSX.utils.book_new()
                  XLSX.utils.book_append_sheet(wb, ws, "Invoices")

                  const tuitionSheetData = tuitionBreakdown.map((data, index) => ({
                    "S No.": index + 1,
                    Month: formatMonth(data.month),
                    "Fee Type": data.category,
                    "Total Invoices": data.invoiceIds.size,
                    "Sub Total": data.subTotal,
                    Discount: data.discount,
                    Fine: data.fine,
                    "Paid Amount": data.paid,
                  }))
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tuitionSheetData), "Tuition Breakdown")

                  const feeGroupSheetData = Object.entries(feeGroupBreakdownMap).map(
                    ([name, data], index) => ({
                      "S No.": index + 1,
                      "Fee Group": name,
                      "Total Invoices": data.invoiceIds.size,
                      "Sub Total": Math.round(data.subTotal),
                      Discount: Math.round(data.discount),
                      Fine: Math.round(data.fine),
                      "Paid Amount": Math.round(data.paid),
                    }),
                  )
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(feeGroupSheetData), "Fee Group Breakdown")

                  const grandTotalSheetData = [
                    {
                      "Total Invoices": activeInvoices.length,
                      "Sub Total": totalSubTotal,
                      Discount: totalDiscount,
                      Fine: totalFine,
                      "Paid Amount": totalCollected,
                    },
                  ]
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(grandTotalSheetData), "Grand Total")

                  XLSX.writeFile(
                    wb,
                    `Fees_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
                  )
                  onClose()
                }}
              >
                <i className="ti ti-download me-2"></i>
                Download Excel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoicesPrintModal
